/**
 * What a paste into Settings > Scripts is, read as it lands. Google Analytics
 * and Meta pixel code become the connected entries Flashroom writes itself, so
 * our events reach them; anything else is a pasted tool, named for it when we
 * know it.
 */
import { GA_ID_RE, PIXEL_ID_RE, SCRIPT_CODE_MAX } from "../../shared/measure";

type PasteReading =
  | { kind: "empty" }
  | { kind: "bad"; message: string }
  | { kind: "ga"; id: string }
  | { kind: "meta"; id: string }
  | { kind: "script"; name: string };

// The tools people paste most, each with what its code always carries.
const KNOWN: Array<[RegExp, string]> = [
  [/googletagmanager\.com\/gtm\.js|['"]GTM-[A-Z0-9]+['"]/i, "Google Tag Manager"],
  [/reb2b/i, "RB2B"],
  [/static\.hotjar\.com|_hjSettings/i, "Hotjar"],
  [/clarity\.ms/i, "Microsoft Clarity"],
  [/analytics\.tiktok\.com|ttq\.load/i, "TikTok pixel"],
  [/snap\.licdn\.com|_linkedin_partner_id/i, "LinkedIn Insight Tag"],
  [/widget\.intercom\.io|intercomSettings/i, "Intercom"],
  [/client\.crisp\.chat/i, "Crisp"],
  [/js\.hs-scripts\.com/i, "HubSpot"],
];

export function readPaste(text: string): PasteReading {
  const t = text.trim();
  if (!t) return { kind: "empty" };
  if (!/<[a-z!]/i.test(t)) return { kind: "bad", message: "This doesn't look like code. Paste the whole snippet, starting with <script>." };
  if (t.length > SCRIPT_CODE_MAX) return { kind: "bad", message: "That is too long. Keep a script under 20,000 characters." };
  const ga = (t.match(/gtag\/js\?id=(G-[A-Z0-9]+)/i) ?? t.match(/gtag\(\s*['"]config['"]\s*,\s*['"](G-[A-Z0-9]+)['"]/i))?.[1]?.toUpperCase();
  if (ga && GA_ID_RE.test(ga)) return { kind: "ga", id: ga };
  const px = t.match(/fbq\(\s*['"]init['"]\s*,\s*['"](\d+)['"]/)?.[1];
  if (px && PIXEL_ID_RE.test(px)) return { kind: "meta", id: px };
  return { kind: "script", name: KNOWN.find(([re]) => re.test(t))?.[1] ?? "" };
}
